import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import useInView from '../hooks/useInView';
import axios from '../api/axios';
import { imageUrl } from '../utils/imageUrl';
import SEO from '../components/SEO';

// ── Individual project card ─────────────────────────────────────────────────
const ProjectCard = ({ project, index }) => {
    const [ref, visible] = useInView();

    return (
        <Link
            ref={ref}
            to={`/gallery/${project._id}`}
            className={`group block overflow-hidden bg-stone-100 transition-all duration-700
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: `${(index % 4) * 100}ms` }}
        >
            {/* Image */}
            <div className="relative overflow-hidden h-72 md:h-80">
                {project.coverImage ? (
                    <img
                        src={imageUrl(project.coverImage)}
                        alt={project.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full bg-stone-200 flex items-center justify-center">
                        <span className="font-sans text-xs tracking-widest uppercase text-stone-400">
                            No Image
                        </span>
                    </div>
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-stone-950/0 group-hover:bg-stone-950/30 transition-all duration-500" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="font-sans text-xs tracking-widest uppercase text-white border border-white/60 px-5 py-2">
                        View Project
                    </span>
                </div>
            </div>

            {/* Caption */}
            <div className="p-5 border-b border-stone-200">
                <h3 className="font-serif text-xl font-light text-stone-800 group-hover:text-jade-600 transition-colors duration-200">
                    {project.name}
                </h3>
                {project.location && (
                    <p className="font-sans text-xs tracking-widest uppercase text-stone-400 mt-1">
                        {project.location}
                    </p>
                )}
            </div>
        </Link>
    );
};

// ── Gallery page ────────────────────────────────────────────────────────────
const Gallery = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [headerRef, headerVisible] = useInView();

    useEffect(() => {
        let mounted = true;
        const fetchProjects = async () => {
            try {
                const res = await axios.get('/gallery');
                if (mounted) setProjects(res.data.projects || []);
            } catch (err) {
                if (mounted) setError('Unable to load projects. Please try again later.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchProjects();
        return () => { mounted = false; };
    }, []);

    return (
        <div>
            <SEO
                title="Projects Gallery"
                description="Browse custom homes and construction projects by Kia Kaha in Squamish, Whistler, and the Sea to Sky corridor. View our portfolio of new builds, renovations, and commercial work."
                keywords="custom home projects Squamish, construction portfolio Whistler, Sea to Sky builds, home gallery Pemberton, contractor projects"
            />
            <PageBanner
                image="images/Kia_Kaha_High_Creek_Web-1.jpg"
                subtitle="Portfolio"
                title="Our Projects"
                position="bg-[center_60%]"
            />

            {/* Intro */}
            <section ref={headerRef} className="section max-w-3xl mx-auto text-center">
                <p
                    className={`font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-4
            transition-all duration-700 ${headerVisible ? 'opacity-100' : 'opacity-0'}`}
                >
                    Kia Kaha Construction
                </p>
                <h2
                    className={`font-serif text-4xl md:text-5xl font-light text-stone-800 mb-4
            transition-all duration-700 delay-100 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    Crafted with Care
                </h2>
                <div className={`divider transition-all duration-700 delay-200 ${headerVisible ? 'opacity-100' : 'opacity-0'}`} />
                <p
                    className={`font-sans text-stone-500 text-base leading-relaxed mt-4
            transition-all duration-700 delay-300 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    Each project in our portfolio represents a unique vision brought to life through
                    skilled craftsmanship, sustainable practices, and an unwavering attention to detail.
                </p>
            </section>

            {/* Grid */}
            <section className="px-6 md:px-12 lg:px-24 pb-24">
                {loading && (
                    <div className="flex justify-center py-24">
                        <div className="w-8 h-8 border-2 border-jade-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}

                {error && (
                    <div className="text-center py-24">
                        <p className="font-sans text-stone-400 text-sm">{error}</p>
                    </div>
                )}

                {!loading && !error && projects.length === 0 && (
                    <div className="text-center py-24 max-w-md mx-auto">
                        <div className="w-px h-16 bg-jade-400 mx-auto mb-8" />
                        <h3 className="font-serif text-3xl font-light text-stone-700 mb-4">
                            Projects Coming Soon
                        </h3>
                        <p className="font-sans text-stone-400 text-sm leading-relaxed">
                            Our portfolio is being curated. Check back soon to explore our work across
                            the Sea to Sky corridor.
                        </p>
                        <div className="mt-8">
                            <Link to="/contact" className="btn-outline">Get In Touch</Link>
                        </div>
                    </div>
                )}

                {!loading && !error && projects.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
                        {projects.map((project, i) => (
                            <ProjectCard key={project._id} project={project} index={i} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Gallery;
